import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Box, Divider, Typography, styled } from '@mui/material';
import { Button, Checkbox } from "../../components";
import CustomCard from "../CustomCardComponent/CustomCard";
import { setReservationData } from '../../features/commerce/commerceSlice';
import { setIsConfirmationPageAccessible } from '../../features/globalStates/globalStatesSlice';
import { confirmReservationApi } from '../../utils/Reservation/ReservationApi';
import { formattedPrice } from '../../utils/Helper';
import { UNAVAILABLE } from '../../constants';
import { generateConsentMessage } from '../../utils/HelperFunctions/OrderAndReservationHelper';

const TypographyTitle = styled(Typography)(({ theme: { breakpoints } }) => `
    font-family: Aeonik Regular;
    font-size: 24px;
    font-style: normal;
    font-weight: 700;
    line-height: 32px;
`);

const TypographyList = styled(Typography)(({ theme: { breakpoints } }) => `
    font-family: Aeonik Regular;
    font-size: 18px;
    font-style: normal;
    font-weight: 400;
    line-height: 28px;
`);

const TypographyText = styled(Typography)(({ theme: { breakpoints } }) => `
    font-family: Aeonik Regular;
    font-size: 16px;
    font-style: normal;
    font-weight: 400;
    line-height: 24px;
`);

const APPROVE_BEFORE_SHIP = "APPROVE_BEFORE_SHIP";

const ReservationSummaryCard = ({totalDoses, listPrice, totalPrice, editReservationID, handleAlert, showContractPrice}) => {
  const [checked, setChecked] = useState(false);
  const history = useHistory();
  const dispatch = useDispatch();
  const locale = useSelector((state) => state.globalStates.locale);
  const reservation = useSelector(store => store?.commerce?.reservation?.reservation);
  const reservationId = reservation?.reservationId;
  const productRows = reservation?.reservationEntries?.filter(row => row?.quantity !== 0);
  const shippingPreference = APPROVE_BEFORE_SHIP;
  const userEmail = useSelector((state) => state.globalStates.emailAddress);
  const reservationUpdate = {
    reservation: {
        ...reservation,
        userShippingPreference: shippingPreference
    }
  };

    const handlePlaceReservation = async () => {
        let confirmReservationData = {
            userId: reservation?.userId,
            reservation: {
                reservationId: reservationId,
                userId: reservation?.userId,
                userShippingPreference: shippingPreference,
            }
        };

        try {
            dispatch(setReservationData(reservationUpdate));
            await dispatch(confirmReservationApi({ payload: confirmReservationData, reservationId: reservationId, userEmail })).unwrap();
            dispatch(setIsConfirmationPageAccessible(true));
            if(!!editReservationID) history.push(`/${locale}/confirmation/${editReservationID}`);
            else history.push(`/${locale}/confirmation`);
        } catch (error) {
            handleAlert()
        } finally {
            await dispatch(setReservationData({ reservation: {}, reservationEntries: [] }));
        }
    };

  const consentMessage = generateConsentMessage();

  return (
    <Box width={'486px'} height={'730px'}>
        <CustomCard sx={{display:'flex', flexDirection: 'column', padding: '32px', overflow: 'scroll'}} >
            <TypographyTitle mb='24px' variant='h3' textTransform='capitalize'>Summary</TypographyTitle>
            <Box display='flex' justifyContent='space-between' alignItems={'center'} width='100%' mb='8px'>
                <TypographyList variant='h5'>Total Doses</TypographyList>
                <TypographyList variant='p1'>{totalDoses}</TypographyList>
            </Box>

            <Box display='flex' justifyContent='space-between' alignItems={'center'} width='100%' mb='8px'>
                <TypographyList variant='h5'>List Price</TypographyList>
                <TypographyList 
                    variant='p1' 
                    sx={{ textDecoration: showContractPrice ? 'line-through' : 'none' }}
                >
                    {formattedPrice(listPrice)}
                </TypographyList>
            </Box>

            <Box display='flex' justifyContent='space-between' alignItems={'center'} width='100%' mb='8px'>
                <TypographyList variant='h5'>Contract Price</TypographyList>
                <TypographyList variant='p1' textAlign={'right'}>{showContractPrice ? formattedPrice(totalPrice) : UNAVAILABLE}</TypographyList>
            </Box>

            <Divider orientation="horizontal" variant="middle" sx={{ marginBottom: 2, marginTop: 1 }} />
            
            <Box display='flex' justifyContent='space-between' alignItems={'center'} width='100%' mb='8px'>
                <TypographyTitle variant='h5'>Estimated Total</TypographyTitle>
                <TypographyTitle variant='p1' textAlign={'right'}>{formattedPrice(totalPrice)}</TypographyTitle>
            </Box>

            <TypographyText mt={2} mb={3}>
            Check back again to see contract pricing. Orders will not be shipped until they have been confirmed and FDA approves or authorizes the 2025-2026 vaccine formula for Moderna COVID-19 Vaccines. You will be notified when it is time to confirm your order.
            </TypographyText>  
            <Box>
                <Checkbox
                    sx={{ alignItems: "flex-start", marginTop: '24px' }}
                    name="privacyPolicyConsent"
                    label={consentMessage}
                    value={checked}
                    data-testid="privacyPolicyConsentCheckbox"
                    onChange={() => setChecked(prev => !prev)}
                />
            </Box>
            <Button 
                buttonType='mds-primary'
                sx={{textTransform: 'none', marginTop: '24px'}}
                onClick={handlePlaceReservation}
                disabled={!checked || productRows?.length === 0}
            >
                {!!editReservationID ? 'Update reservation' : 'Place Reservation'}
            </Button>
        </CustomCard>
    </Box>
  )
}

export default ReservationSummaryCard
